import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Settings, 
  CheckCircle, 
  XCircle, 
  MessageSquare,
  Mail,
  CreditCard,
  Shield,
  Server,
  Key,
  Zap,
  AlertTriangle,
  Info
} from 'lucide-react';
import { apiService } from '../../services/api';

export const ConfiguracoesAvancadasPage = () => {
  const [loading, setLoading] = useState(true);
  const [configuracoes, setConfiguracoes] = useState(null);
  const [configurando, setConfigurando] = useState(false);
  const [activeTab, setActiveTab] = useState("whatsapp");
  
  // Estados para configurações
  const [whatsappConfig, setWhatsappConfig] = useState({
    callmebot_api_key: '',
    ultramsg_token: '',
    ultramsg_instance: ''
  });
  
  const [emailConfig, setEmailConfig] = useState({
    sendgrid_api_key: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: ''
  });
  
  const [pagamentosConfig, setPagamentosConfig] = useState({
    mercadopago_access_token: '',
    stripe_secret_key: '',
    pagseguro_token: ''
  });
  
  const [detranConfig, setDetranConfig] = useState({
    detran_api_key: '',
    detran_endpoint: ''
  });

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const status = await apiService.getStatusConfiguracoes();
      setConfiguracoes(status);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const configurarWhatsApp = async (tipo) => {
    setConfigurando(true);
    try {
      let resultado;
      
      if (tipo === 'callmebot') {
        resultado = await apiService.configurarWhatsAppApiKey(whatsappConfig.callmebot_api_key);
      } else if (tipo === 'ultramsg') {
        resultado = await apiService.configurarUltramsg(
          whatsappConfig.ultramsg_token, 
          whatsappConfig.ultramsg_instance
        );
      }
      
      if (resultado.sucesso) {
        alert(`${tipo} configurado com sucesso!`);
        carregarConfiguracoes();
      }
    } catch (error) {
      console.error(`Erro ao configurar ${tipo}:`, error);
      alert(`Erro ao configurar ${tipo}. Verifique os dados e tente novamente.`);
    } finally {
      setConfigurando(false);
    }
  };

  const configurarEmail = async (tipo) => {
    setConfigurando(true);
    try {
      let resultado;
      
      if (tipo === 'sendgrid') {
        resultado = await apiService.configurarSendGrid(emailConfig.sendgrid_api_key);
      } else if (tipo === 'smtp') {
        resultado = await apiService.configurarSMTP({
          host: emailConfig.smtp_host,
          port: parseInt(emailConfig.smtp_port),
          username: emailConfig.smtp_username,
          password: emailConfig.smtp_password,
          from_email: emailConfig.smtp_from_email
        });
      }
      
      if (resultado.sucesso) {
        alert(`Email ${tipo} configurado com sucesso!`);
        carregarConfiguracoes();
      }
    } catch (error) {
      console.error(`Erro ao configurar ${tipo}:`, error);
      alert(`Erro ao configurar email ${tipo}. Verifique os dados.`);
    } finally {
      setConfigurando(false);
    }
  };

  const configurarPagamento = async (gateway) => {
    setConfigurando(true);
    try {
      let resultado;
      
      if (gateway === 'mercadopago') {
        resultado = await apiService.configurarMercadoPago(pagamentosConfig.mercadopago_access_token);
      } else if (gateway === 'stripe') {
        resultado = await apiService.configurarStripe(pagamentosConfig.stripe_secret_key);
      }
      
      if (resultado.sucesso) {
        alert(`${gateway} configurado com sucesso!`);
        carregarConfiguracoes();
      }
    } catch (error) {
      console.error(`Erro ao configurar ${gateway}:`, error);
      alert(`Erro ao configurar ${gateway}. Verifique os dados.`);
    } finally {
      setConfigurando(false);
    }
  };

  const configurarDetran = async () => {
    setConfigurando(true);
    try {
      const resultado = await apiService.configurarDetranOficial(
        detranConfig.detran_api_key,
        detranConfig.detran_endpoint
      );
      
      if (resultado.sucesso) {
        alert('DETRAN-SP oficial configurado com sucesso!');
        carregarConfiguracoes();
      }
    } catch (error) {
      console.error('Erro ao configurar DETRAN:', error);
      alert('Erro ao configurar DETRAN-SP oficial. Verifique as credenciais.');
    } finally {
      setConfigurando(false);
    }
  };

  const getStatusBadge = (configurado) => {
    return configurado ? (
      <Badge className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Configurado
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800">
        <XCircle className="h-3 w-3 mr-1" />
        Não Configurado
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando configurações...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="h-6 w-6 mr-2 text-blue-600" />
          Configurações Avançadas
        </h2>
      </div>

      {/* Resumo Geral */}
      <Card>
        <CardHeader>
          <CardTitle>Status Geral do Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <MessageSquare className="h-8 w-8 mx-auto text-green-600 mb-2" />
              <p className="font-medium">WhatsApp</p>
              <p className="text-sm text-gray-600">
                {configuracoes?.whatsapp ? 
                  `${Object.values(configuracoes.whatsapp).filter(Boolean).length}/4 APIs` : 
                  '0/4 APIs'
                }
              </p>
            </div>
            
            <div className="text-center">
              <Mail className="h-8 w-8 mx-auto text-blue-600 mb-2" />
              <p className="font-medium">Email</p>
              <p className="text-sm text-gray-600">
                {configuracoes?.email ? 
                  `${Object.values(configuracoes.email).filter(Boolean).length}/2 Provedores` : 
                  '0/2 Provedores'
                }
              </p>
            </div>
            
            <div className="text-center">
              <CreditCard className="h-8 w-8 mx-auto text-purple-600 mb-2" />
              <p className="font-medium">Pagamentos</p>
              <p className="text-sm text-gray-600">
                {configuracoes?.pagamentos ? 
                  `${Object.values(configuracoes.pagamentos).filter(Boolean).length}/3 Gateways` : 
                  '0/3 Gateways'
                }
              </p>
            </div>
            
            <div className="text-center">
              <Shield className="h-8 w-8 mx-auto text-orange-600 mb-2" />
              <p className="font-medium">DETRAN</p>
              <p className="text-sm text-gray-600">
                {configuracoes?.detran ? 
                  `${Object.values(configuracoes.detran).filter(Boolean).length}/2 APIs` : 
                  '0/2 APIs'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs de Configuração */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="pagamentos">Pagamentos</TabsTrigger>
          <TabsTrigger value="detran">DETRAN</TabsTrigger>
        </TabsList>

        {/* WhatsApp Configuration */}
        <TabsContent value="whatsapp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-green-600" />
                Configuração WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* CallMeBot */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">CallMeBot API</h4>
                  {getStatusBadge(configuracoes?.whatsapp?.callmebot)}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>API Key do CallMeBot</Label>
                    <Input
                      type="password"
                      value={whatsappConfig.callmebot_api_key}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, callmebot_api_key: e.target.value})}
                      placeholder="Cole sua API key aqui..."
                    />
                  </div>
                  <Button 
                    onClick={() => configurarWhatsApp('callmebot')}
                    disabled={configurando || !whatsappConfig.callmebot_api_key}
                    size="sm"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar CallMeBot'}
                  </Button>
                </div>
              </div>

              {/* Ultramsg */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Ultramsg API</h4>
                  {getStatusBadge(configuracoes?.whatsapp?.ultramsg)}
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Token</Label>
                      <Input
                        type="password"
                        value={whatsappConfig.ultramsg_token}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, ultramsg_token: e.target.value})}
                        placeholder="Token da API..."
                      />
                    </div>
                    <div>
                      <Label>Instance ID</Label>
                      <Input
                        value={whatsappConfig.ultramsg_instance}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, ultramsg_instance: e.target.value})}
                        placeholder="instance123456"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={() => configurarWhatsApp('ultramsg')}
                    disabled={configurando || !whatsappConfig.ultramsg_token || !whatsappConfig.ultramsg_instance}
                    size="sm"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar Ultramsg'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Configuration */}
        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                Configuração de Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SendGrid */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">SendGrid</h4>
                  {getStatusBadge(configuracoes?.email?.sendgrid)}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>SendGrid API Key</Label>
                    <Input
                      type="password"
                      value={emailConfig.sendgrid_api_key}
                      onChange={(e) => setEmailConfig({...emailConfig, sendgrid_api_key: e.target.value})}
                      placeholder="SG.xxxxxxxxxxxxx"
                    />
                  </div>
                  <Button 
                    onClick={() => configurarEmail('sendgrid')}
                    disabled={configurando || !emailConfig.sendgrid_api_key}
                    size="sm"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar SendGrid'}
                  </Button>
                </div>
              </div>

              {/* SMTP */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">SMTP</h4>
                  {getStatusBadge(configuracoes?.email?.smtp)}
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Host SMTP</Label>
                      <Input
                        value={emailConfig.smtp_host}
                        onChange={(e) => setEmailConfig({...emailConfig, smtp_host: e.target.value})}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <Label>Porta</Label>
                      <Input
                        value={emailConfig.smtp_port}
                        onChange={(e) => setEmailConfig({...emailConfig, smtp_port: e.target.value})}
                        placeholder="587"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Usuário</Label>
                      <Input
                        value={emailConfig.smtp_username}
                        onChange={(e) => setEmailConfig({...emailConfig, smtp_username: e.target.value})}
                        placeholder="seu-email@gmail.com"
                      />
                    </div>
                    <div>
                      <Label>Senha</Label>
                      <Input
                        type="password"
                        value={emailConfig.smtp_password}
                        onChange={(e) => setEmailConfig({...emailConfig, smtp_password: e.target.value})}
                        placeholder="senha do app"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email Remetente</Label>
                    <Input
                      value={emailConfig.smtp_from_email}
                      onChange={(e) => setEmailConfig({...emailConfig, smtp_from_email: e.target.value})}
                      placeholder="noreply@desloca.com"
                    />
                  </div>
                  <Button 
                    onClick={() => configurarEmail('smtp')}
                    disabled={configurando || !emailConfig.smtp_host || !emailConfig.smtp_username}
                    size="sm"
                  >
                    <Server className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar SMTP'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Configuration */}
        <TabsContent value="pagamentos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-purple-600" />
                Configuração de Pagamentos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <strong>Atenção:</strong> Configure pelo menos um gateway para habilitar pagamentos automatizados PIX e cartão de crédito.
                </AlertDescription>
              </Alert>
              
              {/* Mercado Pago */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Mercado Pago</h4>
                  {getStatusBadge(configuracoes?.pagamentos?.mercadopago)}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Access Token</Label>
                    <Input
                      type="password"
                      value={pagamentosConfig.mercadopago_access_token}
                      onChange={(e) => setPagamentosConfig({...pagamentosConfig, mercadopago_access_token: e.target.value})}
                      placeholder="TEST-xxxxx ou APP_USR-xxxxx"
                    />
                  </div>
                  <Button 
                    onClick={() => configurarPagamento('mercadopago')}
                    disabled={configurando || !pagamentosConfig.mercadopago_access_token}
                    size="sm"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar Mercado Pago'}
                  </Button>
                </div>
              </div>

              {/* Stripe */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Stripe</h4>
                  {getStatusBadge(configuracoes?.pagamentos?.stripe)}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Secret Key</Label>
                    <Input
                      type="password"
                      value={pagamentosConfig.stripe_secret_key}
                      onChange={(e) => setPagamentosConfig({...pagamentosConfig, stripe_secret_key: e.target.value})}
                      placeholder="sk_test_xxxxx ou sk_live_xxxxx"
                    />
                  </div>
                  <Button 
                    onClick={() => configurarPagamento('stripe')}
                    disabled={configurando || !pagamentosConfig.stripe_secret_key}
                    size="sm"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar Stripe'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DETRAN Configuration */}
        <TabsContent value="detran" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-orange-600" />
                Configuração DETRAN
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <strong>Importante:</strong> Credenciais oficiais do DETRAN-SP são obtidas mediante convênio oficial. Sistema atual funciona com simulação inteligente.
                </AlertDescription>
              </Alert>
              
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">DETRAN-SP Oficial</h4>
                  {getStatusBadge(configuracoes?.detran?.detran_oficial)}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>API Key Oficial</Label>
                    <Input
                      type="password"
                      value={detranConfig.detran_api_key}
                      onChange={(e) => setDetranConfig({...detranConfig, detran_api_key: e.target.value})}
                      placeholder="Chave fornecida pelo DETRAN-SP"
                    />
                  </div>
                  <div>
                    <Label>Endpoint da API</Label>
                    <Input
                      value={detranConfig.detran_endpoint}
                      onChange={(e) => setDetranConfig({...detranConfig, detran_endpoint: e.target.value})}
                      placeholder="https://api.detran.sp.gov.br/consultas"
                    />
                  </div>
                  <Button 
                    onClick={configurarDetran}
                    disabled={configurando || !detranConfig.detran_api_key || !detranConfig.detran_endpoint}
                    size="sm"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {configurando ? 'Configurando...' : 'Configurar DETRAN Oficial'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};